import { createSlice } from '@reduxjs/toolkit';
import {
  assembleKit,
  disassembleKit,
  fetchKit,
  fetchKitPlan,
  fetchKits,
  findKit,
  scanKitCode,
} from './operatorKitsThunks';
import { kitSourcesShown } from './operatorKitsActions';
import {
  KIT_WORK,
  KIT_STEP,
  isKitBusy,
  kitSourcesProblem,
  staleNeedsQuantity,
} from './operatorKitsUtils';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { intentOf } from '../operator/operatorIdempotency';
import { acceptedCode } from '../operator/operatorScanGate';
import { operatorWarehouseSelected } from '../operator/operatorSlice';

/*
 * Kits on the floor: the station's list, and one visit to a kit — how many,
 * where they come from, where they go, and the write the visit waits for.
 * Nothing is persisted: a reload starts the visit over.
 */
const initialList = {
  items: [],
  count: 0,
  page: 1,
  hasMore: false,
  loading: false,
  error: null,
  requestId: null,
};

const initialKitLot = { lot: '', expiryDate: '' };

/** One piece of work on the kit: «Armar más» starts another. */
const initialWork = {
  step: KIT_STEP.QUANTITY,
  /** As typed. */
  quantity: '',
  plan: null,
  planLoading: false,
  planError: null,
  planRequestId: null,
  /** The place the operator chose instead of the plan's, by product id (the kit's own, taking it apart). */
  preferred: {},
  /** `{code, name, suggested}` once the operator scanned where it all goes. */
  destination: null,
  /** The lot and expiry of the kits being made, when the kit controls them. */
  kitLot: initialKitLot,
  scan: { lastCode: null },
  /** The `requestId` of the destination being read. */
  scanning: null,
  /** How the last write went: `{kind, replayed, quantity, sku, toSlotCode, legs}`. */
  result: null,
};

/** One visit to a kit: opening another starts it over, and a trip to the station does not. */
const initialVisit = {
  kitId: null,
  kit: null,
  loading: false,
  error: null,
  requestId: null,
  mode: KIT_WORK.ASSEMBLE,
  /** `'assemble' | 'disassemble'` while a write is out. */
  pending: null,
  /** `{type, arg, key}` of the write this visit sent last: the only one whose answer it takes. */
  lastWrite: null,
  ...initialWork,
};

const initialState = {
  list: initialList,
  /** The station's reads: one on its way, the last code it took, and the kits a code was several of. */
  station: { finding: false, lastCode: null, choice: null },
  ...initialVisit,
};

const isAwaited = (state, action) => action.meta.requestId === state.lastWrite?.key;

const startWrite = (kind, type) => (state, action) => {
  // A write of another kit — sent again from its band — leaves this visit alone.
  if (action.meta.arg?.kitId !== state.kitId) return;
  state.pending = kind;
  state.lastWrite = { type, arg: intentOf(action.meta.arg), key: action.meta.requestId };
};

const settleWrite = (kind) => (state, action) => {
  if (!isAwaited(state, action)) return;
  const { arg } = action.meta;
  state.pending = null;
  state.lastWrite = null;
  state.kit = action.payload.kit ?? state.kit;
  // A reload out read the kit before this answer: it is dropped.
  state.loading = false;
  state.requestId = null;
  state.result = {
    kind,
    replayed: Boolean(action.payload.replayed),
    quantity: Number(arg.quantity),
    sku: arg.kitSku,
    toSlotCode: arg.toSlotCode,
    legs: action.payload.assembly?.legs || [],
  };
  state.step = KIT_STEP.RESULT;
};

/** A 412 goes back to what changed: the quantity, if it is out of reach now; the sources otherwise. */
const rejectWrite = (state, action) => {
  if (action.meta.condition || !isAwaited(state, action)) return;
  const error = action.payload;
  state.pending = null;
  if (!isRetryable(error)) state.lastWrite = null;
  if (error?.kind !== OPERATOR_ERROR.STALE) return;
  Object.assign(state, { plan: null, planLoading: false, planError: null, planRequestId: null });
  state.step = staleNeedsQuantity(error, state.quantity) ? KIT_STEP.QUANTITY : KIT_STEP.SOURCES;
};

const operatorKitsSlice = createSlice({
  name: 'operatorKits',
  initialState,
  reducers: {
    /** The station opened: a choice it offered before may be stale. Its last code stays. */
    kitStationEntered: (state) => {
      state.station.choice = null;
    },
    kitChoiceDismissed: (state) => {
      state.station.choice = null;
    },
    /**
     * A kit opened. Another kit starts a new visit, seeded with its row of the
     * list while it loads; the same one keeps it, unless its work was done.
     *
     * @param {{payload: number}} action - The kit's id.
     */
    kitEntered: (state, action) => {
      const kitId = action.payload;
      if (state.kitId !== kitId) {
        Object.assign(state, initialVisit, {
          kitId,
          kit: state.list.items.find((row) => row.id === kitId) ?? null,
        });
        return;
      }
      if (state.step === KIT_STEP.RESULT) Object.assign(state, initialWork);
    },
    /** @param {{payload: 'assemble'|'disassemble'}} action */
    kitModeChanged: (state, action) => {
      const mode = action.payload;
      if (!Object.values(KIT_WORK).includes(mode) || mode === state.mode || isKitBusy(state)) return;
      if (mode === KIT_WORK.DISASSEMBLE && state.kit?.disassemble_blocked) return;
      Object.assign(state, initialWork, { mode });
    },
    /** @param {{payload: string}} action - As typed. */
    kitQuantityChanged: (state, action) => {
      if (state.step !== KIT_STEP.QUANTITY || isKitBusy(state)) return;
      state.quantity = action.payload;
    },
    /**
     * «Cambiar» a source: where the operator takes a component — or the kits,
     * taking them apart — from.
     *
     * @param {{payload: {productId: number, key: string}}} action - `key`: a `sourceKeyOf`.
     */
    kitSourceChosen: (state, action) => {
      if (state.step !== KIT_STEP.SOURCES || isKitBusy(state)) return;
      state.preferred[action.payload.productId] = action.payload.key;
    },
    /** «Ya los junté» / «Ya los saqué». */
    kitSourcesConfirmed: (state) => {
      if (state.step !== KIT_STEP.SOURCES || state.planLoading || isKitBusy(state)) return;
      if (kitSourcesProblem(state.plan, state.preferred, state.mode, state.quantity)) return;
      state.step = KIT_STEP.DESTINATION;
    },
    /** «Volver»: one step back, never with a write out or unconfirmed. */
    kitStepBack: (state) => {
      if (isKitBusy(state)) return;
      if (state.step === KIT_STEP.DESTINATION) state.step = KIT_STEP.SOURCES;
      else if (state.step === KIT_STEP.SOURCES) Object.assign(state, { step: KIT_STEP.QUANTITY, planError: null });
    },
    /** @param {{payload: {field: 'lot'|'expiryDate', value: string}}} action */
    kitLotChanged: (state, action) => {
      if (isKitBusy(state)) return;
      state.kitLot[action.payload.field] = action.payload.value;
    },
    /** «Armar más»: the same kit and mode, a new piece of work. */
    kitWorkRestarted: (state) => {
      if (state.step !== KIT_STEP.RESULT) return;
      Object.assign(state, initialWork);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(operatorWarehouseSelected, () => initialState)

      .addCase(fetchKits.pending, (state, action) => {
        state.list.loading = true;
        state.list.error = null;
        state.list.requestId = action.meta.requestId;
      })
      .addCase(fetchKits.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.list.requestId) return;
        const page = action.meta.arg?.page ?? 1;
        const results = action.payload.results || [];
        state.list.loading = false;
        state.list.page = page;
        state.list.items = page === 1 ? results : [...state.list.items, ...results];
        state.list.count = action.payload.count ?? state.list.items.length;
        state.list.hasMore = Boolean(action.payload.next);
      })
      .addCase(fetchKits.rejected, (state, action) => {
        if (action.meta.requestId !== state.list.requestId) return;
        state.list.loading = false;
        state.list.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(fetchKit.pending, (state, action) => {
        if (action.meta.arg !== state.kitId) return;
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchKit.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.kit = action.payload;
      })
      .addCase(fetchKit.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(kitSourcesShown, (state) => {
        if (state.step === KIT_STEP.QUANTITY) state.step = KIT_STEP.SOURCES;
      })
      .addCase(fetchKitPlan.pending, (state, action) => {
        if (action.meta.arg.kitId !== state.kitId) return;
        state.planLoading = true;
        state.planError = null;
        state.planRequestId = action.meta.requestId;
      })
      // Only the last plan asked, and only while it is for what the visit shows.
      .addCase(fetchKitPlan.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.planRequestId) return;
        const { mode, quantity } = action.meta.arg;
        state.planLoading = false;
        if (mode !== state.mode || quantity !== Number(state.quantity)) return;
        state.plan = action.payload;
        if (action.payload.kit) state.kit = action.payload.kit;
        const suggested = action.payload.kit_lot?.suggested_expiry;
        if (suggested && !state.kitLot.expiryDate) state.kitLot.expiryDate = suggested;
      })
      .addCase(fetchKitPlan.rejected, (state, action) => {
        if (action.meta.requestId !== state.planRequestId) return;
        state.planLoading = false;
        state.planError = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(findKit.pending, (state) => {
        state.station.finding = true;
      })
      .addCase(findKit.fulfilled, (state, action) => {
        state.station.finding = false;
        state.station.lastCode = acceptedCode(action);
        if (action.payload.kind === 'choose' && !action.meta.offScreenTask) {
          state.station.choice = {
            message: `${action.payload.code} es de más de un kit: elige cuál.`,
            options: action.payload.options,
          };
        }
      })
      .addCase(findKit.rejected, (state, action) => {
        if (!action.meta.condition) state.station.finding = false;
      })

      .addCase(scanKitCode.pending, (state, action) => {
        state.scanning = action.meta.requestId;
      })
      .addCase(scanKitCode.fulfilled, (state, action) => {
        if (state.scanning !== action.meta.requestId) return;
        state.scanning = null;
        state.scan.lastCode = acceptedCode(action);
        if (action.payload.kitId === state.kitId && state.step === KIT_STEP.DESTINATION) {
          state.destination = action.payload.destination;
        }
      })
      .addCase(scanKitCode.rejected, (state, action) => {
        if (!action.meta.condition && state.scanning === action.meta.requestId) state.scanning = null;
      })

      .addCase(assembleKit.pending, startWrite(KIT_WORK.ASSEMBLE, assembleKit.typePrefix))
      .addCase(assembleKit.fulfilled, settleWrite(KIT_WORK.ASSEMBLE))
      .addCase(assembleKit.rejected, rejectWrite)
      .addCase(disassembleKit.pending, startWrite(KIT_WORK.DISASSEMBLE, disassembleKit.typePrefix))
      .addCase(disassembleKit.fulfilled, settleWrite(KIT_WORK.DISASSEMBLE))
      .addCase(disassembleKit.rejected, rejectWrite);
  },
});

export const {
  kitStationEntered,
  kitChoiceDismissed,
  kitEntered,
  kitModeChanged,
  kitQuantityChanged,
  kitSourceChosen,
  kitSourcesConfirmed,
  kitStepBack,
  kitLotChanged,
  kitWorkRestarted,
} = operatorKitsSlice.actions;

/** A write whose answer was lost: nothing else goes, and no read is taken, until it is sent again. */
export const selectKitUnconfirmed = (state) => {
  const visit = state.operatorKits;
  return Boolean(visit.lastWrite && !visit.pending);
};

/** Whether a read code is welcome right now: only where the result is left, with nothing out. */
export const selectKitAccepting = (state) => {
  const visit = state.operatorKits;
  return visit.step === KIT_STEP.DESTINATION && !isKitBusy(visit);
};

export default operatorKitsSlice.reducer;
